import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { ChevronLink } from '@bibliocommons/base-links';
import { GreenBadge, DarkRedBadge, DarkGreyBadge } from '@bibliocommons/base-badges';

import Heading from 'app/components/shared/Heading';
import { eventShape } from 'app/shapes/events';
import { selectLocationEntities, selectPlaceEntities } from 'app/selectors/EntitiesSelector';
import { getRegistrationStatusMessage } from 'app/helpers/events/utils';
import mapEventToEventCard from 'app/helpers/events/mapEventToEventCard';
import ContentCard from 'app/components/cards/ContentCard';

import './EventsList.scss';

const WAITLIST = 'waitlist';

export default function EventsList({
  sectionHeading, 
  relatedEvents, 
  viewAllHref, 
  uiContainerTitle,
  trackEventLinkClick, 
  trackViewAllEventsLinkClick
}) {
  const intl = useIntl();
  const eventIds = relatedEvents.get('eventIds');
  const events = relatedEvents.get('events');
  const eventLocations = useSelector(selectLocationEntities);
  const places = useSelector(selectPlaceEntities);
  let cardId = 1;
  const viewAllMessage = {
    id: 'events.view.related',
    defaultMessage: `View all related events (${relatedEvents.get('count')})`,
    values: { count: relatedEvents.get('count') }
  };

  const viewAllLinkText = intl.formatMessage(viewAllMessage, viewAllMessage.values);

  function renderBadge(event, registrationStatus) {
    const isCancelled = event.getIn(['definition', 'isCancelled']);
    if (isCancelled) {
      return (
        <span className="event-status-badge">
          <DarkRedBadge size="small">
            <FormattedMessage id="cancelled" />
          </DarkRedBadge>
        </span>
      );
    }

    if (registrationStatus?.state === WAITLIST) {
      return (
        <span className="event-status-badge">
          <GreenBadge size="small">
            <FormattedMessage id="join_waitlist" />
          </GreenBadge>
        </span>
      );
    }

    if (event?.get('isFull')) {
      return (
        <span className="event-status-badge">
          <DarkGreyBadge size="small">
            <FormattedMessage id="registration_full" />
          </DarkGreyBadge>
        </span>
      );
    }

    return null;
  }

  function renderEventCard(id, eventIndex) {
    const event = events.get(id);
    const location = event.getIn(['definition', 'branchLocationId'])
      ? eventLocations.get(event.getIn(['definition', 'branchLocationId']))
      : places.get(event.getIn(['definition', 'nonBranchLocationId']));
    const registrationStatus = getRegistrationStatusMessage(event);
    const card = mapEventToEventCard(cardId, event, location);
    cardId++;

    return (
      <li className="events-list" key={`event-card_${eventIndex}`}>
        <ContentCard
          className="event-card"
          card={card}
          headingTag="h4"
          hasBackground={false}
          hasBorder={false}
          hasType={false}
          dateStampSize="default"
          key={`event-card_${card.get('id')}`}
          renderBadge={() => renderBadge(event, registrationStatus)}
          handleTitleClick={() => trackEventLinkClick({ 
            linkText: event?.getIn(['definition', 'title']),
            eventData: event,
            uiContainerTitle
          })}
        />
      </li>
    );
  }

  return (
    <div className="cp-events-list">
      <Heading className="section-heading" tagName="h3" size="medium" variant>
        {sectionHeading}
      </Heading>
      <div className="events-list-section">{eventIds.map(renderEventCard)}</div>
      <ChevronLink 
        dataKey="all-related-events" 
        className="related-events-link" 
        href={viewAllHref}
        handleClick={() => trackViewAllEventsLinkClick({ linkText: viewAllLinkText, uiContainerTitle })}
      >
        <span aria-hidden>{viewAllLinkText}</span>
      </ChevronLink>
    </div>
  );
}

EventsList.propTypes = {
  sectionHeading: PropTypes.object,
  relatedEvents: ImmutablePropTypes.mapContains({
    eventIds: ImmutablePropTypes.listOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    events: ImmutablePropTypes.mapOf(eventShape),
    count: PropTypes.number
  }).isRequired,
  viewAllHref: PropTypes.string.isRequired,
  uiContainerTitle: PropTypes.string.isRequired,
  trackEventLinkClick: PropTypes.func.isRequired,
  trackViewAllEventsLinkClick: PropTypes.func.isRequired
};
